import { Link } from 'framework7-react';
import { Router } from 'framework7/types';
import { FC, useEffect, useState } from 'react';
import { RegisterMethod } from './RegisterMethod/RegisterMethod';
import style from './RegisterQRPageStyle.module.css';
import { validateAffiliateSignature } from '@/api/registerQRApi';
import CustomerIcon from '@/assets/images/register-qr/customer.png';
import LogoImg from '@/assets/images/register-qr/logo.svg';
import StaffIcon from '@/assets/images/register-qr/staff.png';
import { RegisterQRPageTemplate } from '@/components/uiParts/Template/RegisterQRPageTemplate/RegisterQRPageTemplate';
import { initialState } from '@/config/initialState';
import { paths } from '@/config/paths';
import { useToast } from '@/hooks/useToast';
import { getRegisterQrContext, initializeRegisterQr } from '@/utils/registerQr';
import { storeDispatch } from '@/utils/store';

interface RegisterQRPageProps {
  f7route: Router.Route;
  f7router: Router.Router;
}

export const RegisterQRPage: FC<RegisterQRPageProps> = ({
  f7route,
  f7router,
}) => {
  const { openToast } = useToast();
  const [registrationContext] = useState(() =>
    getRegisterQrContext(f7route.query),
  );
  const [isInitializing, setIsInitializing] = useState(true);

  const redirectInviteCode = () => {
    f7router.navigate(paths.registerNewMember);
  };

  const redirectRegisterNewStaff = () => {
    f7router.navigate(paths.registerNewStaff);
  };

  useEffect(() => {
    let isCurrent = true;

    const initializeRegistration = async () => {
      const result = await initializeRegisterQr(
        registrationContext,
        validateAffiliateSignature,
        () => isCurrent,
      );

      if (result.status === 'stale') {
        return;
      }

      if (result.status === 'invalid') {
        // 無効なQRの加盟店ID・署名を後続の登録へ残さない。
        storeDispatch('setRegisterUser', 'registerUser', {
          ...initialState.registerUser,
        });
        openToast(result.message, 'toast-failed');
        f7router.navigate(paths.loginTutorial, {
          animate: false,
        });
        return;
      }

      // 署名付きQRでは、検証成功後にだけ登録情報を確定する。
      storeDispatch('setRegisterUser', 'registerUser', {
        ...initialState.registerUser,
        isCorporation: false,
        currentStep: 1,
        step1: {
          companyName: '',
          companyNameKana: '',
          firstName: '',
          firstNameKana: '',
          familyName: '',
          familyNameKana: '',
          email: '',
          birthday: '',
          tel1: '',
          tel2: '',
          tel3: '',
          tel4: '',
          tel5: '',
          tel6: '',
          mobile_type1: 0,
          mobile_type2: null,
        },
        step2: {
          zipCode1: '',
          zipCode2: '',
          municipalities: '',
          prefectures: '',
          street: '',
          apartment: '',
        },
        affiliate_id: registrationContext.affiliateId,
        sig: registrationContext.sig,
      });
      setIsInitializing(false);
    };

    initializeRegistration();

    return () => {
      // 遷移後に古い検証結果でstoreや画面を上書きしない。
      isCurrent = false;
    };
  }, [f7router, openToast, registrationContext]);

  // 初回描画から初期化完了までは登録方法を操作させない。
  if (isInitializing) {
    return (
      <RegisterQRPageTemplate
        title=""
        pageName="RegisterQR"
        showNavBack={false}
      >
        <div className={style['register-qr-div']}>
          <img src={LogoImg} />
          <p>
            {registrationContext.sig
              ? 'QRコードを検証中...'
              : '登録情報を確認中...'}
          </p>
        </div>
      </RegisterQRPageTemplate>
    );
  }

  return (
    <RegisterQRPageTemplate title="" pageName="RegisterQR" showNavBack={false}>
      <div>
        <Link
          onClick={() => {
            // backを使うとログアウト後にログインチュートリアルのページに戻れないのでnavigateを使う
            f7router.navigate(paths.loginTutorial, {
              animate: false,
            });
          }}
          className={`${style['no-ripple']} no-ripple`}
        >
          <div>
            <i className="icon-left no-ripple" />
          </div>
        </Link>
        <div className={style['register-qr-div']}>
          <img src={LogoImg} />
          新規会員
        </div>
        <div className={style['register-qr-method']}>
          <RegisterMethod
            handleClick={redirectInviteCode}
            img={CustomerIcon}
            title="個人のお客様"
          />
          <RegisterMethod
            handleClick={redirectRegisterNewStaff}
            img={StaffIcon}
            title="法人のお客様"
          />
        </div>
      </div>
    </RegisterQRPageTemplate>
  );
};
